import { useOrderRefresh } from "./order-refresh";
import { RECORDS_PER_PAGE } from "@taunggyi-plus/domain";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./provider";
export function usePagedRecords<T>(
  resource: string,
  query: string,
  status: string,
  parent: string | null = null,
) {
  const { client } = useAuth();
  const [pagination, setPagination] = useState({ key: "", page: 0 }),
    [items, setItems] = useState<T[]>([]),
    [total, setTotal] = useState(0),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [loadedKey, setLoadedKey] = useState(""),
    [revision, setRevision] = useState(0);
  const key = `${resource}:${query}:${status}:${parent}`;
  const effectivePage = pagination.key === key ? pagination.page : 0;
  const setPage = useCallback((page: number) => setPagination({ key, page }), [key]);
  useEffect(() => {
    if (!client) return;
    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      void client
        .rpc("business_records_page", { resource, query, status, parent, page: effectivePage })
        .then(async (r) => {
          if (!active) return;
          if (r.error) {
            setError(r.error.message);
            setItems([]);
          } else {
            const data = r.data as { items: T[]; total: number };
            const target = new URLSearchParams(window.location.search).get("record");
            if (
              target &&
              /^[0-9a-f-]{36}$/i.test(target) &&
              !data.items.some((item) => (item as { id?: string }).id === target)
            ) {
              const detail = await client.rpc("business_record_detail", {
                resource,
                target,
                parent,
              });
              if (!active) return;
              if (!detail.error && detail.data) data.items = [detail.data as T, ...data.items];
            }
            setItems((previous) =>
              effectivePage === 0
                ? data.items
                : [
                    ...previous,
                    ...data.items.filter(
                      (item) =>
                        !previous.some(
                          (old) => (old as { id?: string }).id === (item as { id?: string }).id,
                        ),
                    ),
                  ],
            );
            setTotal(data.total);
            setError("");
            if (effectivePage > 0 && data.items.length === 0)
              setPage(Math.max(0, Math.ceil(data.total / RECORDS_PER_PAGE) - 1));
          }
          setLoadedKey(`${key}:${effectivePage}`);
          setLoading(false);
        })
        .catch(() => {
          if (active) {
            setError("Could not load records. Please retry.");
            setLoadedKey(`${key}:${effectivePage}`);
            setLoading(false);
          }
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [client, resource, query, status, parent, effectivePage, revision, setPage, key]);
  const refresh = useCallback(() => setRevision((r) => r + 1), []);
  useOrderRefresh(refresh);
  return {
    items,
    total,
    error,
    loading: loading || loadedKey !== `${key}:${effectivePage}`,
    page: effectivePage,
    setPage,
    refresh,
  };
}
